<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'ChatMateKnowledge' }" class="btn btn-secondary">
          <i class="ri-arrow-left-line"></i>
          返回列表
        </router-link>
        <template v-if="knowledge">
          <button
            v-if="knowledge.s3_key"
            type="button"
            class="btn btn-secondary"
            @click="handleDownload"
            :disabled="downloading"
          >
            <i class="ri-download-line"></i>
            <span v-if="downloading">取得連結中...</span>
            <span v-else>下載原始檔案</span>
          </button>
          <button
            v-if="knowledge.training_status === 'failed' || knowledge.training_status === 'pending'"
            type="button"
            class="btn btn-primary"
            @click="handleTrain"
            :disabled="training"
          >
            <i class="ri-refresh-line"></i>
            <span v-if="training">訓練中...</span>
            <span v-else>重新訓練</span>
          </button>
          <button type="button" class="btn btn-danger" @click="handleDelete">
            <i class="ri-delete-bin-line"></i>
            刪除知識庫
          </button>
        </template>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchKnowledge">重試</button>
    </div>

    <div v-else-if="knowledge" class="detail-content">
      <div class="detail-section">
        <h2>基本資訊</h2>
        <dl class="detail-list">
          <dt>標題</dt>
          <dd>{{ knowledge.title }}</dd>
          
          <dt>檔案類型</dt>
          <dd>
            <span class="badge badge-info">{{ getContentTypeText(knowledge.content_type) }}</span>
          </dd>
          
          <dt>檔案大小</dt>
          <dd>{{ formatFileSize(knowledge.file_size) }}</dd>
          
          <dt>分塊數量</dt>
          <dd>{{ knowledge.chunk_count || 0 }}</dd>
          
          <dt>訓練狀態</dt>
          <dd>
            <span :class="getTrainingStatusClass(knowledge.training_status)">
              {{ getTrainingStatusText(knowledge.training_status) }}
            </span>
          </dd>
          
          <dt>建立時間</dt>
          <dd>{{ formatDate(knowledge.created_at) }}</dd>
          
          <dt>更新時間</dt>
          <dd>{{ formatDate(knowledge.updated_at) }}</dd>
        </dl>
      </div>

      <div v-if="knowledge.tags && knowledge.tags.length > 0" class="detail-section">
        <h2>標籤</h2>
        <div class="tags-list">
          <span v-for="tag in knowledge.tags" :key="tag" class="badge badge-secondary">
            {{ tag }}
          </span>
        </div>
      </div>

      <div v-if="knowledge.content" class="detail-section">
        <div class="content-header">
          <h2>文件內容</h2>
          <div class="content-meta">
            <span class="content-length">{{ knowledge.content.length.toLocaleString() }} 字元</span>
            <button type="button" class="btn-toggle" @click="contentExpanded = !contentExpanded">
              <i :class="contentExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
              {{ contentExpanded ? '收合' : '展開' }}
            </button>
          </div>
        </div>
        <div class="content-preview" :class="{ expanded: contentExpanded }">
          <pre>{{ knowledge.content }}</pre>
        </div>
        <button v-if="!contentExpanded" type="button" class="btn-expand-hint" @click="contentExpanded = true">
          顯示完整內容
        </button>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { chatmateService } from '@/api/modules/chatmate.js';
import { showError, showDeleteConfirm, showSuccess } from '@/utils/swal.js';

export default {
  name: 'ChatMateKnowledgeDetail',
  setup() {
    const { pageTitle } = usePageTitle('知識庫詳情');
    const route = useRoute();
    const router = useRouter();
    const knowledge = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const training = ref(false);
    const downloading = ref(false);
    const contentExpanded = ref(false);

    const fetchKnowledge = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await chatmateService.getKnowledge(route.params.id);

        if (response && response.success && response.data) {
          knowledge.value = response.data;
        } else {
          error.value = response?.message || '獲取知識庫詳情失敗';
        }
      } catch (err) {
        console.error('獲取知識庫詳情失敗:', err);
        error.value = err.response?.data?.message || err.message || '獲取知識庫詳情失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleTrain = async () => {
      if (!knowledge.value) return;

      training.value = true;

      try {
        const response = await chatmateService.trainKnowledge(knowledge.value.id);

        if (response && response.success) {
          await showSuccess('訓練已開始', '知識庫訓練已開始，請稍後查看訓練狀態');
          // 更新訓練狀態
          knowledge.value.training_status = 'processing';
          // 可以選擇自動刷新或讓使用者手動刷新
          setTimeout(() => {
            fetchKnowledge();
          }, 2000);
        } else {
          await showError('訓練失敗', response?.message || '觸發訓練失敗');
        }
      } catch (err) {
        console.error('觸發訓練失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '觸發訓練失敗';
        await showError('訓練失敗', errorMessage);
      } finally {
        training.value = false;
      }
    };

    const handleDownload = async () => {
      if (downloading.value) return;
      downloading.value = true;
      try {
        const response = await chatmateService.getKnowledgeDownloadUrl(knowledge.value.id);
        if (response && response.success && response.data?.url) {
          const a = document.createElement('a');
          a.href = response.data.url;
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          await showError('下載失敗', response?.message || '無法取得下載連結');
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || '下載失敗';
        await showError('下載失敗', msg);
      } finally {
        downloading.value = false;
      }
    };

    const handleDelete = async () => {
      if (!knowledge.value) return;

      try {
        const confirmed = await showDeleteConfirm(knowledge.value.title);
        if (!confirmed) return;

        const response = await chatmateService.deleteKnowledge(knowledge.value.id);

        if (response && response.success) {
          await showSuccess('刪除成功', '知識庫已刪除');
          router.push({ name: 'ChatMateKnowledge' });
        } else {
          await showError('刪除失敗', response?.message || '刪除知識庫失敗');
        }
      } catch (err) {
        console.error('刪除知識庫失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '刪除知識庫失敗';
        await showError('刪除失敗', errorMessage);
      }
    };

    const getContentTypeText = (type) => {
      const typeMap = {
        text: '文字',
        faq: 'FAQ',
        markdown: 'Markdown',
        pdf: 'PDF',
        docx: 'DOCX',
        xlsx: 'Excel',
        json: 'JSON',
        url: 'URL'
      };
      return typeMap[type] || type;
    };

    const getTrainingStatusText = (status) => {
      const statusMap = {
        pending: '待處理',
        processing: '處理中',
        completed: '已完成',
        failed: '失敗'
      };
      return statusMap[status] || status || '未知';
    };

    const getTrainingStatusClass = (status) => {
      const classMap = {
        pending: 'badge badge-secondary',
        processing: 'badge badge-warning',
        completed: 'badge badge-success',
        failed: 'badge badge-danger'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const formatFileSize = (bytes) => {
      if (!bytes) return '-';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    onMounted(() => {
      fetchKnowledge();
    });

    return {
      pageTitle,
      knowledge,
      loading,
      error,
      training,
      downloading,
      contentExpanded,
      fetchKnowledge,
      handleDownload,
      handleTrain,
      handleDelete,
      getContentTypeText,
      getTrainingStatusText,
      getTrainingStatusClass,
      formatFileSize,
      formatDate
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.detail-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.detail-section {
  margin-bottom: 32px;
}

.detail-section h2 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 8px;
}

.detail-list {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 16px;
}

.detail-list dt {
  font-weight: 600;
  color: #666;
}

.detail-list dd {
  margin: 0;
  color: #333;
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 8px;
}

.content-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
  border: none;
  padding: 0;
}

.content-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.content-length {
  font-size: 13px;
  color: #999;
}

.btn-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-toggle:hover {
  border-color: #007bff;
  color: #007bff;
}

.content-preview {
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 16px;
  max-height: 300px;
  overflow: hidden;
  position: relative;
  transition: max-height 0.3s ease;
}

.content-preview.expanded {
  max-height: none;
  overflow: auto;
}

.content-preview:not(.expanded)::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: linear-gradient(transparent, #f8f9fa);
  pointer-events: none;
}

.content-preview pre {
  margin: 0;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
}

.btn-expand-hint {
  display: block;
  width: 100%;
  margin-top: 8px;
  background: none;
  border: none;
  color: #007bff;
  font-size: 13px;
  cursor: pointer;
  text-align: center;
  padding: 6px;
}

.btn-expand-hint:hover {
  text-decoration: underline;
}
</style>
